import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { OrganizationSwitcher } from "@/components/OrganizationSwitcher";
import TransitionOverlay from "@/components/TransitionOverlay";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { signOutUser } from "@/lib/auth";
import { functions, storage } from "@/lib/firebase";
import { markTosViewBypassed } from "@/lib/tosBypass";
import { TosType } from "@/types/termsOfService";
import { getDownloadURL, ref } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

// Retry and timeout configuration
const TOTAL_TIMEOUT_MS = 10000; // 10 seconds total across all retries
const MAX_RETRIES = 3;
const BASE_RETRY_DELAY_MS = 500; // Exponential backoff: 500ms, 1s, 2s

interface AcceptTermsOfServiceProps {
  /**
   * Offer the organization switcher, so a member of several orgs is not stuck
   * on one org's terms. Off for hosts whose org is fixed by the URL (annotation links).
   */
  allowOrganizationSwitch?: boolean;
}

export default function AcceptTermsOfService({ allowOrganizationSwitch = false }: AcceptTermsOfServiceProps) {
  const { toast } = useToast();
  const { selectedOrganization, tosVersions, user } = useAuth();
  const [tosContent, setTosContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);

  // Determine ToS type and version
  const orgTosType = (selectedOrganization?.tosType as TosType) || "standardDataSharing";
  const currentVersion = tosVersions?.[orgTosType as keyof Pick<typeof tosVersions, "standardDataSharing" | "standardNoDataSharing">];
  const fullVersionId = currentVersion ? `${orgTosType}-v${currentVersion}` : null;

  // Fetch ToS content from Firebase Storage with retry logic
  useEffect(() => {
    async function fetchTosContent(): Promise<void> {
      if (!fullVersionId) {
        setError("Terms of Service version not configured. Please contact support.");
        setLoading(false);
        return;
      }

      const startTime = Date.now();
      const storagePath = `termsOfService/${fullVersionId}.md`;

      console.log("[ToSLoading] Starting ToS fetch", {
        storagePath,
        totalTimeoutMs: TOTAL_TIMEOUT_MS,
        maxRetries: MAX_RETRIES,
      });

      // Create AbortController for overall timeout
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      timeoutIdRef.current = setTimeout(() => {
        console.error(`[ToSLoading] Overall timeout exceeded (${TOTAL_TIMEOUT_MS}ms)`);
        abortControllerRef.current?.abort();
      }, TOTAL_TIMEOUT_MS);

      setLoading(true);
      setError(null);
      retryCountRef.current = 0;

      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        retryCountRef.current = attempt;

        if (signal.aborted) {
          break;
        }

        console.log(`[ToSLoading] Attempt ${attempt}/${MAX_RETRIES}`, {
          elapsedMs: Date.now() - startTime,
        });

        try {
          const fileRef = ref(storage, storagePath);
          const downloadURL = await getDownloadURL(fileRef);

          if (signal.aborted) {
            break;
          }

          const response = await fetch(downloadURL, {
            cache: "no-store",
            signal,
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to fetch Terms of Service content`);
          }

          const markdown = await response.text();

          console.log("[ToSLoading] Success", {
            attempt,
            contentLength: markdown.length,
            elapsedMs: Date.now() - startTime,
          });

          if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
          setTosContent(markdown);
          setLoading(false);
          return;
        } catch (err: any) {
          lastError = err;

          // Don't retry on abort
          if (signal.aborted || err.name === "AbortError") {
            console.error("[ToSLoading] Aborted", { attempt, elapsedMs: Date.now() - startTime });
            break;
          }

          // Don't retry on 404 (file not found)
          if (err.code === "storage/object-not-found") {
            console.error("[ToSLoading] File not found", { storagePath });
            break;
          }

          console.warn(`[ToSLoading] Attempt ${attempt} failed`, {
            error: err.message,
            code: err.code,
            elapsedMs: Date.now() - startTime,
          });

          // Wait before retrying with exponential backoff
          if (attempt < MAX_RETRIES) {
            const delay = BASE_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }

      // All retries exhausted or non-retryable error
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

      console.error("[ToSLoading] All retries exhausted", {
        attempts: retryCountRef.current,
        elapsedMs: Date.now() - startTime,
        lastError: lastError?.message,
      });

      if (lastError?.code === "storage/object-not-found") {
        setError("Terms of Service document not found. Please contact support.");
      } else {
        setError("Failed to load Terms of Service. Please try again.");
      }
      setLoading(false);
    }

    fetchTosContent();

    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      abortControllerRef.current?.abort();
    };
  }, [fullVersionId]);

  async function handleAccept(): Promise<void> {
    if (!selectedOrganization?.id || !fullVersionId) {
      toast({
        title: "Error",
        description: "Missing organization or version information.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);

    try {
      const acceptTermsOfService = httpsCallable(functions, "acceptTermsOfService");
      const result = await acceptTermsOfService({
        orgId: selectedOrganization.id,
        version: fullVersionId,
      });

      const data = result.data as { success: boolean; error?: { code: string; message: string } };

      if (data.success) {
        toast({
          title: "Terms Accepted",
          description: "Thank you for accepting the Terms of Service.",
        });
        window.location.reload();
        return;
      }

      if (!data.error) {
        return;
      }

      // Handle specific error codes
      if (data.error.code === "VERSION_MISMATCH") {
        toast({
          title: "Version Updated",
          description: "A new version is available. Please refresh and try again.",
          variant: "destructive",
        });
        window.location.reload();
      } else if (data.error.code === "ALREADY_ACCEPTED") {
        toast({
          title: "Already Accepted",
          description: "You have already accepted these terms.",
        });
        window.location.reload();
      } else {
        toast({
          title: "Error",
          description: data.error.message,
          variant: "destructive",
        });
      }
    } catch (err: any) {
      console.error("Error accepting ToS:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to accept Terms of Service. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  function handleContinueAnyway(): void {
    console.warn("[ToSLoading] User continuing without viewing ToS due to load failure", {
      userId: user?.uid,
      orgId: selectedOrganization?.id,
      fullVersionId,
      retryCount: retryCountRef.current,
    });

    toast({
      title: "Continuing to app",
      description: "You'll be asked to review the Terms of Service next time you log in.",
    });

    if (selectedOrganization?.id) {
      markTosViewBypassed(selectedOrganization.id);
    }
    window.location.reload();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading Terms of Service...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Show bypass option for network/timeout failures (not for config errors like missing version)
    const canBypass = !error.includes("not configured");

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="w-full max-w-md space-y-6 bg-white p-8 rounded-lg shadow text-center">
          <div className="text-4xl mb-2">
            <span role="img" aria-label="warning">&#x26A0;&#xFE0F;</span>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            We're having trouble loading the Terms of Service
          </h1>
          <p className="text-muted-foreground">
            {error.includes("contact support") ? (
              <>
                {error.replace("Please contact support.", "Please ")}<a href="mailto:support@gleanag.com" className="underline hover:text-foreground">contact support</a>.
              </>
            ) : (
              error
            )}
            {canBypass && " This may be due to your network connection."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => window.location.reload()}>Retry</Button>
            {canBypass && (
              <Button variant="outline" onClick={handleContinueAnyway}>
                Continue Anyway to Glean
              </Button>
            )}
          </div>
          {canBypass && (
            <p className="text-sm text-muted-foreground">
              You will be prompted to accept the Terms of Service on your next login.
            </p>
          )}
          <div className="pt-4 border-t flex flex-wrap items-center justify-center gap-3">
            {allowOrganizationSwitch && <OrganizationSwitcher />}
            <button
              onClick={() => signOutUser()}
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <TransitionOverlay
        open={submitting}
        title="Accepting Terms..."
        subtitle="Please wait..."
      />
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="w-full max-w-3xl bg-white rounded-lg shadow overflow-hidden flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="p-6 border-b">
            <div className="flex flex-wrap items-center justify-end gap-2">
              {allowOrganizationSwitch && <OrganizationSwitcher />}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => signOutUser()}
                className="text-muted-foreground hover:text-foreground"
              >
                Log Out
              </Button>
            </div>
            <h1 className="text-2xl font-semibold text-center">Glean Ag Terms of Service</h1>
            <p className="text-muted-foreground text-center mt-2">
              Please review and accept the Terms of Service to continue.
            </p>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 prose prose-sm max-w-none">
            <ReactMarkdown>{tosContent}</ReactMarkdown>
          </div>

          {/* Footer with checkbox and button */}
          <div className="p-6 border-t bg-gray-50 space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="agree"
                checked={hasAgreed}
                onCheckedChange={(checked) => setHasAgreed(checked === true)}
                className="mt-1"
              />
              <label htmlFor="agree" className="text-sm cursor-pointer">
                I have read and agree to the Terms of Service. I understand that my use of Glean Ag
                is subject to these terms.
              </label>
            </div>
            <Button
              onClick={handleAccept}
              disabled={!hasAgreed || submitting}
              className="w-full"
            >
              {submitting ? "Accepting..." : "Accept and Continue"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
